import { useState } from 'react';
import { CheckCircle2, CircleX } from 'lucide-react';
import { Checkbox } from '@/components/ui/checkbox';
import { StatusBadge } from '@/components/ui/status-badge';
import { OnboardingTable } from '@/components/ui/onboarding-table';
import { OnboardingMultiEmailInput } from './OnboardingMultiEmailInput';
import OnboardingProjectHeader from './OnboardingProjectHeader';
import { InfoAlert } from './onboarding-layout';
import type { CodeAnalysisResult } from '@/utils/filterFilePaths';
import { FileItem } from '../api/detectRepository';

interface FileExclusionsCardProps {
  org: string;
  project: string;
  repo: string;
  branch: string;
  instrumentedFilesData: CodeAnalysisResult;
  onFilesSelected: (files: Set<FileItem>) => void;
  prEmails: string[];
  onPrEmailsChange: (emails: string[]) => void;
  autoPrEmails: string[];
  onAutoPrEmailsChange: (emails: string[]) => void;
  skipPrEmails: boolean;
  onSkipPrEmailsChange: (skip: boolean) => void;
  skipAutoPrEmails: boolean;
  onSkipAutoPrEmailsChange: (skip: boolean) => void;
  prEmailsError?: string;
  autoPrEmailsError?: string;
}

const PROCESSING_EXTENSIONS = [
  '.js',
  '.ts',
  '.jsx',
  '.tsx',
  '.mjs',
  '.cjs',
  '.cs',
  '.py',
];
const IGNORED_EXTENSIONS = [
  '.json',
  '.md',
  '.css',
  '.scss',
  '.html',
  '.svg',
  '.png',
  '.yml',
];

function getFileExtension(filePath: string): string {
  return `.${filePath.split('.').pop() ?? ''}`;
}

interface FileRow extends FileItem {
  _ext: string;
  _large: boolean;
}

function FileExclusionsCard({
  org,
  project,
  repo,
  branch,
  instrumentedFilesData,
  onFilesSelected,
  prEmails,
  onPrEmailsChange,
  autoPrEmails,
  onAutoPrEmailsChange,
  skipPrEmails,
  onSkipPrEmailsChange,
  skipAutoPrEmails,
  onSkipAutoPrEmailsChange,
  prEmailsError,
  autoPrEmailsError,
}: FileExclusionsCardProps) {
  const allFiles: FileItem[] = [
    ...instrumentedFilesData.nonTestFiles,
    ...instrumentedFilesData.nonTestlargerFiles,
  ];

  const tableData: FileRow[] = [
    ...instrumentedFilesData.nonTestlargerFiles.map(f => ({
      ...f,
      _ext: getFileExtension(f.path),
      _large: true,
    })),
    ...instrumentedFilesData.nonTestFiles.map(f => ({
      ...f,
      _ext: getFileExtension(f.path),
      _large: false,
    })),
  ];

  const [selectedPaths, setSelectedPaths] = useState<Set<string>>(
    new Set(allFiles.map(f => f.path)),
  );

  const selectedCount = selectedPaths.size;
  const excludedCount = allFiles.length - selectedCount;
  const allChecked = selectedCount === allFiles.length;
  const indeterminate = selectedCount > 0 && selectedCount < allFiles.length;
  const nonInstrumentedCount =
    instrumentedFilesData.invalidExtensionCount +
    instrumentedFilesData.testFilesCount;

  const notifyParent = (paths: Set<string>) => {
    const selected = new Set(allFiles.filter(f => paths.has(f.path)));
    onFilesSelected(selected);
  };

  const toggleFile = (path: string, checked: boolean) => {
    const next = new Set(selectedPaths);
    if (checked) next.add(path);
    else next.delete(path);
    setSelectedPaths(next);
    notifyParent(next);
  };

  const toggleAll = (checked: boolean) => {
    const next = checked
      ? new Set(allFiles.map(f => f.path))
      : new Set<string>();
    setSelectedPaths(next);
    notifyParent(next);
  };

  return (
    <div>
      <OnboardingProjectHeader
        org={org}
        project={project}
        repoName={repo}
        branchName={branch}
      />

      <InfoAlert className="mb-6">
        Analyzing repository files. Only supported source files will be processed
        for coverage analysis.
      </InfoAlert>

      <div className="mb-5 flex gap-4 max-[768px]:flex-col">
        <div className="flex-1 rounded-xl border border-solid border-[var(--inv-border-soft)] border-l-4 border-l-[var(--inv-accent)] bg-[var(--inv-accent-soft-2,#fff5f0)] p-3.5">
          <div className="mb-2.5 flex items-center gap-2 text-[0.9rem] font-semibold text-[var(--inv-text)]">
            <CheckCircle2 className="h-[1.1rem] w-[1.1rem] text-[#2e7d32]" />
            Processing
          </div>
          <div className="mb-2.5 flex flex-wrap gap-1.5">
            {PROCESSING_EXTENSIONS.map(ext => (
              <span
                key={ext}
                className="inline-block rounded-full border border-solid border-[#bdbdbd] bg-white px-[0.55rem] py-0.5 font-mono text-[0.78rem] text-[#333]">
                {ext}
              </span>
            ))}
          </div>
          <div className="text-[0.8rem] font-medium text-[var(--inv-accent)]">
            <strong>{selectedCount}</strong> files will be instrumented
          </div>
        </div>

        <div className="flex-1 rounded-xl border border-solid border-[var(--inv-border)] border-l-4 border-l-[var(--inv-text-muted)] bg-[var(--inv-surface-muted)] p-3.5">
          <div className="mb-2.5 flex items-center gap-2 text-[0.9rem] font-semibold text-[var(--inv-text)]">
            <CircleX className="h-[1.1rem] w-[1.1rem] text-[#c62828]" />
            Ignoring
          </div>
          <div className="mb-2.5 flex flex-wrap gap-1.5">
            {IGNORED_EXTENSIONS.map(ext => (
              <span
                key={ext}
                className="inline-block rounded-full border border-solid border-[#bdbdbd] bg-white px-[0.55rem] py-0.5 font-mono text-[0.78rem] text-[#333]">
                {ext}
              </span>
            ))}
          </div>
          <div className="text-[0.8rem] text-[#53565a]">
            <strong>{nonInstrumentedCount}</strong> files auto-ignored
          </div>
        </div>
      </div>

      <div className="mb-5 flex items-center gap-0 rounded-xl border border-solid border-[var(--inv-border)] bg-[var(--inv-surface-muted)] px-6 py-3">
        <div className="flex items-baseline gap-2">
          <span className="text-[1.4rem] font-bold text-[var(--inv-text)]">
            {instrumentedFilesData.totalFiles}
          </span>
          <span className="text-[0.8rem] text-[var(--inv-text-secondary)]">
            Total Files Detected
          </span>
        </div>
        <div className="mx-6 h-6 w-px shrink-0 bg-[var(--inv-border)]" />
        <div className="flex items-baseline gap-2">
          <span className="text-[1.4rem] font-bold text-[var(--inv-accent)]">
            {selectedCount}
          </span>
          <span className="text-[0.8rem] text-[var(--inv-text-secondary)]">
            To Process
          </span>
        </div>
        <div className="mx-6 h-6 w-px shrink-0 bg-[var(--inv-border)]" />
        <div className="flex items-baseline gap-2">
          <span className="text-[1.4rem] font-bold text-[var(--inv-text)]">
            {nonInstrumentedCount + excludedCount}
          </span>
          <span className="text-[0.8rem] text-[var(--inv-text-secondary)]">
            Ignored
          </span>
        </div>
        <div className="mx-6 h-6 w-px shrink-0 bg-[var(--inv-border)]" />
        <div className="flex items-baseline gap-2">
          <span className="text-[1.4rem] font-bold text-[var(--inv-text)]">
            {excludedCount}
          </span>
          <span className="text-[0.8rem] text-[var(--inv-text-secondary)]">
            User Excluded
          </span>
        </div>
      </div>

      <p className="mb-2 text-sm text-[#53565a]">
        Uncheck to exclude specific files from instrumentation
      </p>

      <div className="mb-3 max-h-[340px] overflow-y-auto rounded-xl border border-solid border-[var(--inv-border)]">
        <OnboardingTable
          className="mb-0 rounded-none border-0 shadow-none"
          data={tableData}
          rowKey={row => row.path}
          selectedKeys={selectedPaths}
          columns={[
            {
              key: 'path',
              header: (
                <Checkbox
                  checked={allChecked}
                  indeterminate={indeterminate}
                  onChange={val => toggleAll(val)}>
                  <span>File Path</span>
                </Checkbox>
              ),
              render: (row: FileRow) => (
                <Checkbox
                  checked={selectedPaths.has(row.path)}
                  onChange={val => toggleFile(row.path, val)}>
                  <p className="m-0 font-mono text-sm">{row.path}</p>
                </Checkbox>
              ),
            },
            {
              key: 'type',
              header: 'Type',
              render: (row: FileRow) => (
                <StatusBadge value={row._ext} variant="primary-outlined" />
              ),
            },
            {
              key: 'size',
              header: 'Size',
              render: (row: FileRow) =>
                row._large ? (
                  <StatusBadge value=">1mb" variant="info-outlined" />
                ) : null,
            },
          ]}
        />
      </div>

      <div className="mb-5 text-[0.8rem] text-[#53565a]">
        <strong>{selectedCount}</strong> files selected for inventory •{' '}
        <strong>{excludedCount}</strong> excluded by you
      </div>

      <div className="mb-6 flex flex-col gap-4 pt-5">
        <div className="flex flex-col gap-2">
          <div className="flex items-center justify-between gap-4">
            <span className="text-sm font-medium">PR Reviewer Emails</span>
            <Checkbox
              checked={skipPrEmails}
              onChange={val => onSkipPrEmailsChange(val)}>
              Skip — proceed without reviewer emails
            </Checkbox>
          </div>
          {!skipPrEmails && (
            <OnboardingMultiEmailInput
              label=""
              placeholder="reviewer@example.com"
              value={prEmails}
              onChange={onPrEmailsChange}
              error={prEmailsError}
            />
          )}
          {skipPrEmails && (
            <p className="m-0 rounded border-l-[3px] border-l-[#e0e0e0] bg-[#f5f5f5] px-3 py-2 text-[0.8rem] text-[#53565a]">
              No PR reviewer emails will be added. You can still proceed to
              scan.
            </p>
          )}
        </div>

        <div className="flex flex-col gap-2">
          <div className="flex items-center justify-between gap-4">
            <span className="text-sm font-medium">
              PR Reviewer Emails (Automation)
            </span>
            <Checkbox
              checked={skipAutoPrEmails}
              onChange={val => onSkipAutoPrEmailsChange(val)}>
              Skip — proceed without reviewer emails
            </Checkbox>
          </div>
          {!skipAutoPrEmails && (
            <OnboardingMultiEmailInput
              label=""
              placeholder="automation-reviewer@example.com"
              value={autoPrEmails}
              onChange={onAutoPrEmailsChange}
              error={autoPrEmailsError}
            />
          )}
          {skipAutoPrEmails && (
            <p className="m-0 rounded border-l-[3px] border-l-[#e0e0e0] bg-[#f5f5f5] px-3 py-2 text-[0.8rem] text-[#53565a]">
              No automation PR reviewer emails will be added. You can still
              proceed to scan.
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

export default FileExclusionsCard;
